// src/pages/terms-and-conditions/page.tsx

import React from "react";
// Import Link from react-router-dom
import { Link } from "react-router-dom";

export default function TermsAndConditions(): React.ReactElement {
    return (
        <div className="min-h-screen bg-gradient-to-br from-[#f0fafa] via-[#e0f2f2] to-[#d0e8e8] pt-28 md:pt-36 pb-12 px-4">
            <div className="max-w-4xl mx-auto animate-fadeIn">
                {/* Header */}
                <div className="text-center mb-12">
                    <span className="inline-block px-3 py-1 text-[10px] font-bold tracking-widest uppercase rounded-full bg-gradient-to-r from-[#174143] to-[#3d9ea4] text-white mb-3 shadow-md">
                        Legal
                    </span>
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        Terms & Conditions
                    </h1>
                    <p className="text-gray-600">Last updated: 25/09/2025</p>
                </div>

                {/* Quick Links */}
                <div className="flex flex-wrap justify-center gap-4 mb-10">
                    {/* Use Link for internal route navigation */}
                    <Link to="/privacy-policy" className="px-6 py-2.5 rounded-full bg-white/50 backdrop-blur-sm text-[#174143] text-sm font-semibold hover:bg-[#174143] hover:text-white transition-all duration-300 shadow-sm hover:shadow-md border border-white/50">
                        Privacy Policy
                    </Link>
                    <Link to="/terms-and-conditions" className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#174143] to-[#3d9ea4] text-white text-sm font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
                        Terms & Conditions
                    </Link>
                    {/* Use standard <a> tag for hash link to the Home page section */}
                    <a href="/#contact" className="px-6 py-2.5 rounded-full bg-white/50 backdrop-blur-sm text-[#174143] text-sm font-semibold hover:bg-[#174143] hover:text-white transition-all duration-300 shadow-sm hover:shadow-md border border-white/50">
                        Contact
                    </a>
                </div>

                {/* Content Card */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50 p-6 md:p-10 space-y-8">
                    <section>
                        <h2 className="text-xl font-bold text-[#174143] mb-4 flex items-center gap-2">
                            <span className="w-1.5 h-6 bg-[#3d9ea4] rounded-full"></span>
                            Terms of Service
                        </h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            By downloading or using the Vidyasamrudhi mobile application (“App”), you agree to these Terms & Conditions. Please read them carefully.
                        </p>
                    </section>

                    <div className="grid md:grid-cols-2 gap-6">
                        <section className="bg-white/50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-[#174143]/10 flex items-center justify-center text-[#174143] text-sm">01</span>
                                Use of the App
                            </h3>
                            <ul className="space-y-2 text-gray-600 text-sm">
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span>Provided for educational purposes only.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span>Do not misuse (hacking, copying, redistributing).</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span>Ensure device meets system requirements.</span>
                                </li>
                            </ul>
                        </section>

                        <section className="bg-white/50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-[#174143]/10 flex items-center justify-center text-[#174143] text-sm">02</span>
                                Accounts
                            </h3>
                            <ul className="space-y-2 text-gray-600 text-sm">
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span>Provide accurate information for account creation.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span>Keep credentials secure; do not share login details.</span>
                                </li>
                            </ul>
                        </section>

                        <section className="bg-white/50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-[#174143]/10 flex items-center justify-center text-[#174143] text-sm">03</span>
                                Content Ownership
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                All videos, lessons, and study materials are owned or licensed by Vidyasamrudhi. You may not copy, distribute, or use content outside the App without express permission.
                            </p>
                        </section>

                        <section className="bg-white/50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-[#174143]/10 flex items-center justify-center text-[#174143] text-sm">04</span>
                                Payments
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                Prices for paid content are shown prior to purchase. Payments are processed through Google Play Billing. Refunds follow Google Play’s policies.
                            </p>
                        </section>
                    </div>

                    <section>
                        <h3 className="text-lg font-bold text-gray-900 mb-3">5. Limitation of Liability</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            We aim to provide accurate learning resources, but we are not liable for errors, interruptions, or losses resulting from App use. To the fullest extent permitted by law, we disclaim liability for indirect, incidental, special, or consequential damages.
                        </p>
                    </section>

                    <div className="grid md:grid-cols-2 gap-8">
                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3">6. Termination</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                We may suspend or terminate accounts that violate these Terms. You may stop using the App at any time.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3">7. Governing Law</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                These Terms are governed by and construed in accordance with the laws of <strong>India</strong>, without regard to conflict of law principles.
                            </p>
                        </section>
                    </div>

                    <section className="bg-[#174143]/5 rounded-xl p-6 text-center mt-8">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">8. Contact Us</h3>
                        <p className="text-gray-600 text-sm mb-4">
                            For any questions about these Terms, please contact us via email.
                        </p>
                        <a href="mailto:vidyasamruddhi2025@gmail.com" className="inline-flex items-center gap-2 px-6 py-2 bg-[#174143] text-white rounded-full font-semibold hover:bg-[#1f5961] transition-colors shadow-lg hover:shadow-xl">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            vidyasamruddhi2025@gmail.com
                        </a>
                    </section>
                </div>
            </div>
        </div>
    );
}